import { useEffect, useRef } from "react";
import MapboxGeocoder, { Result } from "@mapbox/mapbox-gl-geocoder";
import "@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css";
import { Map } from "mapbox-gl";

const accessToken =
  "pk.eyJ1IjoiYnVwcG9vODUiLCJhIjoiY2xzaGV6M2d6MjV4ajJudDAwOXo1bjMwcSJ9.0LeBUIZbR4UWWloslEZjlA";

interface GeocoderResultEvent {
    result: Result;
}

export const useMapboxGeocoder = (map: React.MutableRefObject<Map | null>, mapReady: boolean) => {
    const geocoderContainerRef = useRef<HTMLDivElement | null>(null);
    const geocoderRef = useRef<MapboxGeocoder | null>(null);

    const handleGeocoderResult = (e: GeocoderResultEvent) => {
        const { result } = e;
        if (map.current && result && result.center) {
            map.current.flyTo({
                center: { lat: result.center[1], lng: result.center[0] },
                zoom: 12,
                essential: true,
            });
        }
    };

    const clearGeocoder = () => {
        if (geocoderRef.current) {
            // Clear the input of the geocoder
            geocoderRef.current.clear();
        }
    };

    useEffect(() => {
        if (!mapReady || !map.current || !geocoderContainerRef.current) return;

        const geocoder = new MapboxGeocoder({
            accessToken: accessToken,
            marker: false,
            placeholder: "Search for a location",
        });

        geocoderRef.current = geocoder;

        // Append the geocoder to the container
        geocoderContainerRef.current.appendChild(geocoder.onAdd(map.current));

        geocoder.on("result", handleGeocoderResult as any);

        // Cleanup function
        return () => {
            geocoder.off("result", handleGeocoderResult as any);
            // Detach from the map too (its proximity tracking listens on
            // moveend), so a search box that mounts again doesn't stack them.
            try {
                geocoder.onRemove();
            } catch {
                // Its element may already have gone with the container.
            }
            if (geocoderRef.current === geocoder) geocoderRef.current = null;
        };
    }, [mapReady, map]);

    return { geocoderContainerRef, handleGeocoderResult, clearGeocoder };
};